var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useContext, useEffect, useRef } from 'react';
import { createPortal, findDOMNode } from 'react-dom';
import Transition from 'react-transition-group/Transition';
import { MODAL_VIEW } from './types';
import { ModalContext } from './context';
import { isHTMLElement } from "../helpers/reflect";
import { Overlay, OVERLAY_CONTENT_MOD, OVERLAY_VIEW } from "../Overlay";
import { PageViewPort } from './PageViewport';
import { CLASS_MODAL_INERT_ATTR_NAME } from "../helpers/attributes";
var DEFAULT_TIMEOUT = 0;
var getParentDOMNodeWithScroll = function (parentDOMNode) {
    if (!parentDOMNode) {
        return null;
    }
    var clientWidth = parentDOMNode.clientWidth, clientHeight = parentDOMNode.clientHeight, scrollWidth = parentDOMNode.scrollWidth, scrollHeight = parentDOMNode.scrollHeight;
    if (clientWidth === undefined) {
        return null;
    }
    if (clientWidth || clientHeight) {
        var isCanScroll = ['overflow', 'overflow-x', 'overflow-y'].some(function (key) {
            return /auto|scroll/.test(window.getComputedStyle(parentDOMNode).getPropertyValue(key));
        });
        if (isCanScroll && (clientWidth !== scrollWidth || clientHeight !== scrollHeight)) {
            return parentDOMNode;
        }
    }
    if (parentDOMNode.parentElement) {
        return getParentDOMNodeWithScroll(parentDOMNode.parentElement);
    }
    return null;
};
var disableContent = function (element) {
    if (!element) {
        return;
    }
    element.setAttribute(CLASS_MODAL_INERT_ATTR_NAME, '');
};
var enableContent = function (element) {
    if (!element) {
        return;
    }
    element.removeAttribute(CLASS_MODAL_INERT_ATTR_NAME);
};
var Modality = /** @class */ (function (_super) {
    __extends(Modality, _super);
    function Modality(props) {
        var _this = _super.call(this, props) || this;
        _this.isBodyScrollDisabled = false;
        _this.mainContentElement = null;
        _this.hide = function () {
            var onRequestClose = _this.props.onRequestClose;
            if (onRequestClose) {
                onRequestClose();
                return;
            }
            _this.internalHide();
        };
        _this.onDidLeaveHandler = function () {
            if (!_this.state.isShown) {
                _this.enableMainContent();
                if (_this.props.onDidClose) {
                    _this.props.onDidClose();
                }
            }
        };
        _this.show = function () {
            _this.disableMainContent();
            _this.setState({
                isShown: true
            });
        };
        _this.internalHide = function () {
            _this.setState({
                isShown: false
            });
        };
        _this.state = {
            isShown: !!props.isShown
        };
        return _this;
    }
    Modality.prototype.componentDidMount = function () {
        if (this.state.isShown) {
            this.disableMainContent();
        }
    };
    Modality.prototype.getSnapshotBeforeUpdate = function (prevProps) {
        return { isShownPropsChanged: this.props.isShown !== prevProps.isShown };
    };
    Modality.prototype.componentDidUpdate = function (_, __, snapshot) {
        if (snapshot && snapshot.isShownPropsChanged) {
            if (this.props.isShown) {
                this.show();
            }
            else {
                this.internalHide();
            }
        }
    };
    Modality.prototype.componentWillUnmount = function () {
        this.enableMainContent();
    };
    Modality.prototype.render = function () {
        var isShown = this.state.isShown;
        if (typeof document === 'undefined') {
            return null;
        }
        var render = this.props.render || this.props.children;
        if (!render) {
            return null;
        }
        var injectedProps = {
            isShown: isShown,
            hide: this.hide
        };
        var portalDomNode = this.getPortalDOMNode();
        if (!portalDomNode) {
            return null;
        }
        return createPortal(React.createElement(Transition, {
            in: isShown,
            unmountOnExit: true,
            mountOnEnter: true,
            appear: true,
            timeout: {
                enter: this.props.enterTimeout || DEFAULT_TIMEOUT,
                exit: this.props.exitTimeout || DEFAULT_TIMEOUT
            },
            onExited: this.onDidLeaveHandler
        }, function (state) { return render(__assign(__assign({}, injectedProps), { state: state })); }), portalDomNode);
    };
    Modality.prototype.disableRootScroll = function () {
        if (typeof document !== 'undefined' && !this.isBodyScrollDisabled) {
            var parentDOMNodeWithScroll = this.getParentDOMNodeWithScroll();
            this.isBodyScrollDisabled = true;
            this.prevBodyOverflow = parentDOMNodeWithScroll.style.overflow;
            parentDOMNodeWithScroll.style.overflow = 'hidden';
        }
    };
    Modality.prototype.enableRootScroll = function () {
        if (typeof document !== 'undefined' && this.isBodyScrollDisabled) {
            this.isBodyScrollDisabled = false;
            this.getParentDOMNodeWithScroll().style.overflow = this.prevBodyOverflow;
        }
    };
    Modality.prototype.getParentDOMNodeWithScroll = function () {
        if (!this.parentDOMNodeWithScroll) {
            var selfNode = this.getSelfNode();
            var node = selfNode && selfNode.parentElement
                ? getParentDOMNodeWithScroll(selfNode.parentElement)
                : null;
            this.parentDOMNodeWithScroll = node || document.getElementsByTagName('body')[0];
        }
        return this.parentDOMNodeWithScroll;
    };
    Modality.prototype.getPortalDOMNode = function () {
        return this.context.getPortalDomNode();
    };
    Modality.prototype.disableMainContent = function () {
        if (window !== undefined) {
            this.disableRootScroll();
            disableContent(this.getMainContainerElement());
            var prevModalEl = this.context.getLastFromStack();
            if (prevModalEl) {
                disableContent(prevModalEl);
            }
        }
    };
    Modality.prototype.enableMainContent = function () {
        var _a;
        if (window !== undefined) {
            var prevModalEl = this.context.getPrevFromStack();
            if (!prevModalEl && ((_a = this.context.getLastFromStack()) === null || _a === void 0 ? void 0 : _a.contains(this.getSelfNode()))) {
                this.enableRootScroll();
                enableContent(this.mainContentElement);
            }
            if (prevModalEl) {
                enableContent(prevModalEl);
            }
        }
    };
    Modality.prototype.getMainContainerElement = function () {
        if (!this.props.mainContentId) {
            return null;
        }
        if (!this.mainContentElement) {
            this.mainContentElement = document.getElementById(this.props.mainContentId);
        }
        return this.mainContentElement;
    };
    Modality.prototype.getSelfNode = function () {
        try {
            // eslint-disable-next-line react/no-find-dom-node
            return findDOMNode(this);
        }
        catch (e) {
            return null;
        }
    };
    return Modality;
}(React.PureComponent));
Modality.contextType = ModalContext;
var ModalRoot = function (_a) {
    var onMount = _a.onMount, onUnmount = _a.onUnmount, children = _a.children;
    useEffect(function () {
        onMount();
        return function () {
            onUnmount();
        };
    }, []);
    return children;
};
export var Modal = function (_a) {
    var renderProp = _a.render, children = _a.children, isShown = _a.isShown, onRequestClose = _a.onRequestClose, onDidClose = _a.onDidClose, _b = _a.view, view = _b === void 0 ? MODAL_VIEW.dialog : _b, overlayColor = _a.overlayColor, mainContentIdProp = _a.mainContentId, rest = __rest(_a, ["render", "children", "isShown", "onRequestClose", "onDidClose", "view", "overlayColor", "mainContentId"]);
    var elementRef = useRef(null);
    var _c = useContext(ModalContext), mainContentId = _c.mainContentId, addToStack = _c.addToStack, removeFromStack = _c.removeFromStack, notify = _c.notify, addListener = _c.addListener, getLastFromStack = _c.getLastFromStack;
    var removeListenerRef = useRef(function () { });
    var isTopModalRef = useRef(true);
    var render = renderProp || children;
    if (!render) {
        return null;
    }
    useEffect(function () {
        return addListener(function () {
            isTopModalRef.current = getLastFromStack() === elementRef.current;
        });
    }, []);
    useEffect(function () {
        function handler(e) {
            var _a, _b;
            if (!isHTMLElement(e.target) || !isHTMLElement(elementRef.current)) {
                return;
            }
            if (!elementRef.current.contains(e.target) && isTopModalRef.current) {
                e.preventDefault();
                e.stopPropagation();
                e.target.blur();
                (_b = (_a = elementRef.current.firstChild) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a);
            }
        }
        document.body.addEventListener('focusin', handler);
        return function () {
            document.body.removeEventListener('focusin', handler);
        };
    }, []);
    var isDialog = view === MODAL_VIEW.dialog;
    return (React.createElement(Modality, { isShown: isShown, enterTimeout: 0, exitTimeout: 0, onRequestClose: onRequestClose, onDidClose: onDidClose, mainContentId: mainContentIdProp || mainContentId, render: function (injectProps) {
            return (React.createElement(ModalRoot, { onMount: function () {
                    var el = elementRef.current;
                    if (!el || !isHTMLElement(el)) {
                        return;
                    }
                    addToStack(el);
                    removeListenerRef.current = addListener(function () {
                        setTimeout(function () {
                            var _a, _b, _c;
                            if ((_a = getLastFromStack()) === null || _a === void 0 ? void 0 : _a.contains(el)) {
                                (_c = (_b = el === null || el === void 0 ? void 0 : el.firstChild) === null || _b === void 0 ? void 0 : _b.focus) === null || _c === void 0 ? void 0 : _c.call(_b);
                            }
                        }, 0);
                    });
                    notify();
                }, onUnmount: function () {
                    removeFromStack(elementRef.current);
                    removeListenerRef.current();
                    notify();
                } }, isDialog ? (React.createElement(Overlay, { fixed: true, view: OVERLAY_VIEW.tone, contentMod: OVERLAY_CONTENT_MOD.center, ref: elementRef, "data-selector-id": rest['data-selector-id'] || 'modal-overlay', color: overlayColor }, render(injectProps) || null)) : (React.createElement(PageViewPort, { color: overlayColor, isFullPage: view === MODAL_VIEW.fullPage, "data-selector-id": rest['data-selector-id'] || 'modal-page', ref: elementRef }, render(injectProps) || null))));
        } }));
};
//# sourceMappingURL=Modal.js.map